import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  afterNextRender,
  effect,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as am5 from '@amcharts/amcharts5';
import { ModeAffichageGraphique } from '../../Services/installation';
import { ParcService, ParcChartPoint, ParcChartLabels } from '../../Services/parc';
import { EtatService } from '../../Services/etat';
import { ChartHostOverlayComponent } from '../chart-host-overlay/chart-host-overlay';
import { ChartPeriodSelectorComponent, PeriodeChangee } from '../chart-period-selector/chart-period-selector';
import { ChartFullscreenModalComponent } from '../chart-fullscreen-modal/chart-fullscreen-modal';
import { createChart as createProductionChart } from '../../Pages/installation/installation-chart-puissance';

@Component({
  selector: 'app-parc-graphe-production',
  imports: [ChartHostOverlayComponent, ChartPeriodSelectorComponent, ChartFullscreenModalComponent],
  templateUrl: './parc-graphe-production.html',
  styleUrl: './parc-graphe-production.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParcGrapheProductionComponent implements OnDestroy {
  private readonly parcService = inject(ParcService);
  private readonly etat = inject(EtatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platformId = inject(PLATFORM_ID);

  private chartRoot?: am5.Root;
  private chartHost?: HTMLDivElement;
  private chartData: ParcChartPoint[] = [];
  private chartLabels: ParcChartLabels = {
    production: { libelle: 'Production' },
    ensoleillement: { libelle: 'Ensoleillement' }
  };
  private requeteGraphiqueCourante = 0;

  chargementGraphique = true;
  menuOuvert = false;
  modalVisible = false;
  modalRefreshToken = 0;
  modeAffichage: ModeAffichageGraphique = 'jour';
  dateFin = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return d; })();

  readonly chartRenderer = (host: HTMLDivElement): am5.Root =>
    createProductionChart(host, {
      data: this.chartData,
      production: this.chartLabels.production,
      ensoleillement: this.chartLabels.ensoleillement,
      mode: this.modeAffichage,
      afficherCodeDansTitre: false,
    });

  constructor() {
    // Recharge le graphique quand le parc sélectionné change.
    effect(() => {
      const parc = this.etat.parcSelectionne();
      const requeteId = ++this.requeteGraphiqueCourante;
      this.chartData = [];
      this.chartRoot?.dispose();
      this.chartRoot = undefined;
      this.modalVisible = false;
      if (!parc) {
        this.chargementGraphique = false;
        this.cdr.markForCheck();
        return;
      }
      this.chargementGraphique = true;
      this.cdr.markForCheck();
      void this.chargerEtRendreGraphique(parc.id, requeteId);
    });
  }

  ngOnDestroy() {
    ++this.requeteGraphiqueCourante;
    this.chartRoot?.dispose();
  }

  @HostListener('document:click')
  fermerMenus() {
    this.menuOuvert = false;
    this.cdr.markForCheck();
  }

  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    this.menuOuvert = !this.menuOuvert;
  }

  ouvrirModal() {
    this.menuOuvert = false;
    this.modalVisible = true;
    this.cdr.markForCheck();
  }

  onChartReady(host: HTMLDivElement) {
    this.chartHost = host;
    if (!isPlatformBrowser(this.platformId)) return;
    this.initialiserGraphique();
  }

  onPeriodeChange({ mode, dateFin }: PeriodeChangee) {
    this.modeAffichage = mode;
    this.dateFin = dateFin;
    const parc = this.etat.parcSelectionne();
    if (!parc) return;
    const requeteId = ++this.requeteGraphiqueCourante;
    this.chargementGraphique = true;
    this.cdr.markForCheck();
    void this.chargerEtRendreGraphique(parc.id, requeteId);
  }

  private async chargerEtRendreGraphique(parcId: number, requeteId: number) {
    try {
      const { data, labels } = await this.parcService.getParcIndicateurs(parcId, this.modeAffichage, this.dateFin);
      if (requeteId !== this.requeteGraphiqueCourante || parcId !== this.etat.parcSelectionne()?.id) return;
      this.chartData = data;
      this.chartLabels = labels;
      this.initialiserGraphique(requeteId);
    } catch {
      if (requeteId === this.requeteGraphiqueCourante) {
        this.chargementGraphique = false;
        this.cdr.markForCheck();
      }
    }
  }

  private initialiserGraphique(requeteId?: number) {
    const host = this.chartHost;
    if (!host) {
      if (requeteId == null || requeteId === this.requeteGraphiqueCourante) {
        this.chargementGraphique = false;
        this.cdr.markForCheck();
      }
      return;
    }

    try {
      this.chartRoot?.dispose();
      this.chartRoot = createProductionChart(host, {
        data: this.chartData,
        production: this.chartLabels.production,
        ensoleillement: this.chartLabels.ensoleillement,
        mode: this.modeAffichage,
        afficherCodeDansTitre: false,
      });
    } finally {
      setTimeout(() => {
        if (requeteId == null || requeteId === this.requeteGraphiqueCourante) {
          this.chargementGraphique = false;
          if (this.modalVisible) this.modalRefreshToken++;
          this.cdr.markForCheck();
        }
      }, 0);
    }
  }
}
